"use client";

import { handlePostAnalytics, analyticEvents } from "@/apiUtils/analyticsUtils";
import {
  getFirstWorkingDay,
  formatDateString,
  constructEyeTestMyInfoUrl,
} from "@/apiUtils/commonUtils";
import { dateFormats, analyticEventTypes, EyeTestEventActions } from "@/apiUtils/constants";
import { getTimeSlots } from "@/apiUtils/storeUtils";
import { getPublicHolidays } from "@/apiUtils/mReserveUtils";
import { EyeTestServiceType, DateSectionContent, EyeTestTimeSlot } from "@/common/lib/types";
import { BookingDetailsWithServiceId } from "@/api/mReserve/types";
import { FC, useState, useMemo, useCallback, useEffect } from "react";
import EyeTestDateView from "./view";
import { useRouter, useSearchParams, useParams } from "next/navigation";
import LoadingOverlay from "@/components/ui/loadingOverlay";

interface EyeTestDateProps {
  serviceType?: EyeTestServiceType;
  serviceId?: number;
  storeId?: string;
  content?: DateSectionContent;
  bookingId?: string;
  bookingDetails?: BookingDetailsWithServiceId; // ADD: Accept booking details
  initialHolidays?: Date[];
}

const EyeTestDate: FC<EyeTestDateProps> = ({
  storeId: propStoreId,
  serviceType,
  serviceId: propServiceId,
  content,
  bookingId,
  bookingDetails, // ADD: Extract booking details
  initialHolidays,
}) => {
  const searchParams = useSearchParams();
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || "en";
  const [isMonthChanging, setIsMonthChanging] = useState<boolean>(false);

  // Get storeId and serviceId from URL params or props
  const storeId = propStoreId || searchParams.get("store") || undefined;
  const serviceIdFromUrl = useMemo(() => {
    const urlServiceId = searchParams.get("serviceId");
    return urlServiceId ? Number(urlServiceId) : undefined;
  }, [searchParams]);

  // Use serviceId from prop first, then fall back to URL param
  const serviceId = propServiceId || serviceIdFromUrl;

  // Extract booked date and time from booking details (for rescheduling)
  const bookedDate = useMemo(() => {
    if (bookingDetails?.date) { // CHANGE: Use 'date' instead of 'bookingDate'
      const date = new Date(bookingDetails.date);
      // Reset time to avoid timezone issues
      date.setHours(0, 0, 0, 0);
      return date;
    }
    return null;
  }, [bookingDetails]);

  const bookedSlotTime = useMemo(() => {
    if (bookingDetails?.startTime && bookingDetails?.endTime) {
      // Extract time in HH:mm format from startTime (could be "HH:mm" or "HH:mm:ss" or ISO string)
      let startTime = bookingDetails.startTime;
      if (startTime.includes("T")) {
        startTime = startTime.split("T")[1]?.substring(0, 5) || startTime;
      } else if (startTime.includes(":")) {
        startTime = startTime.substring(0, 5);
      }
      
      let endTime = bookingDetails.endTime;
      if (endTime.includes("T")) {
        endTime = endTime.split("T")[1]?.substring(0, 5) || endTime;
      } else if (endTime.includes(":")) {
        endTime = endTime.substring(0, 5);
      }
      
      return { start: startTime, end: endTime };
    }
    return null;
  }, [bookingDetails]);

  // Extract string values for stable dependencies
  const bookedStartTime = bookedSlotTime?.start;
  const bookedEndTime = bookedSlotTime?.end;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [slotLoading, setSlotLoading] = useState<boolean>(true);
  const [holidays, setHolidays] = useState<Date[] | undefined>(initialHolidays);

  // Initialize selectedDate: use booked date if rescheduling, otherwise use first working day
  const initialDate = useMemo(() => {
    if (bookedDate) {
      // If booked date is in the past, use today's first working day
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (bookedDate >= today) {
        return bookedDate;
      }
    }
    return getFirstWorkingDay(new Date(), initialHolidays) ?? new Date();
  }, [bookedDate, initialHolidays]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<EyeTestTimeSlot | undefined>(undefined);
  const [slots, setSlots] = useState<EyeTestTimeSlot[] | undefined>(undefined);

  const router = useRouter();

  const updateTimeSlots = useCallback(
    async (dateString: string, storeId: string, serviceId?: number) => {
      setSlotLoading(true);
      const timeSlotResponse = await getTimeSlots(storeId, dateString, serviceId, localeString);
      if (timeSlotResponse) {
        setSlots(timeSlotResponse);
        
        // Find first available slot that is NOT the already booked slot
        let firstAvailableSlot = timeSlotResponse.find(slot => {
          // Check if slot is available
          if (!slot.isAvailable) return false;
          
          // If we have a booked slot time, exclude it
          if (bookedStartTime && bookedEndTime) {
            // Compare slot times (format: "HH:mm")
            const isBookedSlot = slot.start === bookedStartTime && slot.end === bookedEndTime;
            return !isBookedSlot;
          }
          
          return true;
        });
        
        setSelectedTimeSlot(firstAvailableSlot || undefined);
      }
      setSlotLoading(false);
    },
    [localeString, bookedStartTime, bookedEndTime], // Use string values instead of object
  );

  // UPDATED: Use serviceId (from prop or URL) instead of just serviceIdFromUrl
  useEffect(() => {
    if (storeId && serviceId && selectedDate) {
      updateTimeSlots(formatDateString(selectedDate, dateFormats.YYYY_MM_DD), storeId, serviceId);
    }
  }, [storeId, serviceId, updateTimeSlots, selectedDate]);

  const onDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Fetch holidays and slots for first working day when month changes
  const onMonthChange = useCallback(
    async (date: Date) => {
      if (storeId && serviceId) {
        try {
          setIsMonthChanging(true);
          setSlotLoading(true);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        // Fetch holidays for the new month
        const fetchedHolidays = await getPublicHolidays(storeId, month, year, localeString);
        setHolidays(fetchedHolidays);

        // Find first working day of the new month
        const firstDayOfMonth = new Date(year, month - 1, 1);
        let firstWorkingDay = getFirstWorkingDay(firstDayOfMonth, fetchedHolidays);

        // Ensure we don't select a date in the past - use today or later
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to compare dates only

        if (firstWorkingDay < today) {
          // If first working day of month is in the past, use today's first working day
          firstWorkingDay = getFirstWorkingDay(today, fetchedHolidays);
        }

        // If rescheduling and booked date is in this month, prefer it
        if (bookedDate && bookedDate.getMonth() === month - 1 && bookedDate.getFullYear() === year && bookedDate >= today) {
          firstWorkingDay = bookedDate;
        }

        // Update selected date to first working day (today or later)
        setSelectedDate(firstWorkingDay);

        // Fetch slots for the first working day
        const dateString = formatDateString(firstWorkingDay, dateFormats.YYYY_MM_DD);
          await updateTimeSlots(dateString, storeId, serviceId);
        } catch (error) {
          console.error("Error fetching public holidays:", error);
        } finally {
          setIsMonthChanging(false);
          setSlotLoading(false);
        }
      }
    },
    [storeId, serviceId, localeString, updateTimeSlots, bookedDate] // ADD bookedDate to dependencies
  );

  const onSlotChange = (slot: EyeTestTimeSlot) => {
    setSelectedTimeSlot(slot);
  };

  const onSubmit = () => {
    if (selectedDate && selectedTimeSlot && storeId && serviceId) {
      setIsLoading(true);
      const date = formatDateString(selectedDate, dateFormats.YYYY_MM_DD);

      handlePostAnalytics({
        eventType: analyticEventTypes.CUSTOM,
        data: {
          target: analyticEvents.BOOK_EYE_TEST,
          bookEyeTestData: {
            eventAction: EyeTestEventActions.DATE_AND_TIME,
            eventLabel: date + " - " + selectedTimeSlot.start,
          },
        },
      });

      router.push(
        constructEyeTestMyInfoUrl(storeId, date, selectedTimeSlot, serviceType, bookingId, serviceId, localeString),
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading || isMonthChanging ? <LoadingOverlay /> : ""}
      <EyeTestDateView
        isLoading={isLoading}
        content={content}
        slots={slots}
        selectedDate={selectedDate}
        selectedTimeSlot={selectedTimeSlot}
        holidays={holidays}
        slotLoading={slotLoading}
        onDateChange={onDateChange}
        onSlotChange={onSlotChange}
        onSubmit={onSubmit}
        onMonthChange={onMonthChange}
      />
    </>
  );
};

export default EyeTestDate;
